import { put, call, all, delay, takeEvery, takeLatest } from 'redux-saga/effects'
import { withCallback } from 'redux-saga-callback'
import { store as reduxStore } from 'index'
import { push } from 'react-router-redux'
import {
  list as groupsList,
  add as groupAdd,
  get as groupGet,
  update as groupUpdate,
  del as groupDelete,
  search as groupsSearch,
  searchMembers as groupMemberSearch,
  members as groupGetMembers,
  addMembers as addMembersToGroup,
  searchBanMembers as groupBannedMemberSearch,
  getBanMembers as groupGetBannedMembers,
  banMember as groupBanMember,
  kickMember as groupKickMember,
  unbanMember as groupUnbanMember,
  listGroupScopePermissions,
  getGroupScopePermissionsValues,
  updateGroupScopePermissions,
  resetGroupScopePermission,
  ChatApp,
  ChatGroup,
  GroupsListFilters,
  GroupsListResponse,
  GroupResponse,
  SuccessMessageResponse,
  GroupMembersListResponse,
  GroupMembersChangeResponse,
  ScopePermissionsListResponse,
  ScopePermissionsValuesResponse,
} from 'services/groups'
import {
  list as usersList,
  search as usersSearch,
  updateScopeInGroup as changeScopeInGroup,
} from 'services/users'
import { UsersListResponse, GroupMembersChangeResponse as UserGroupMembersChangeResponse } from 'services/users/types'
import _ from 'lodash'
import { getAppHost } from 'helpers'
import actions from './actions'
import { GroupsState, LoosePagination } from './reducers'

/**
 * Groups slice sagas. Ported from the dormant redux/groups/sagas.js, which is
 * itself ported from the legacy `redux/apps/sagas.js` GROUP section — line-by-line
 * reconciled to parity (docs/groups-migration/PARITY-AUDIT.md) before this .ts
 * conversion (GROUPS-MIGRATION-PLAN.md D4). Types derived from captured staging
 * responses: docs/groups-migration/Api-response/.
 *
 * Deliberate D2 exception (documented in PARITY-AUDIT.md): UNBAN_GROUP_MEMBER's
 * error branch intentionally does NOT reproduce two live apps/sagas.js bugs
 * (loading flag never reset on failure; one error path fires a "success"-typed
 * toast) — this file keeps the dormant version's already-correct behavior.
 *
 * NON_PARTICIPANTS_LIST/SEARCH_NON_PARTICIPANT and CHANGE_MEMBER_SCOPE
 * deliberately call into `services/users` (generic list/search, and
 * `updateScopeInGroup` from services/users/groups.ts respectively) — this is
 * the real, live production behavior (confirmed by reading apps/sagas.js
 * directly), not a services/groups.ts responsibility. Reusing these exports
 * doesn't violate D5 (which only bars modifying services/users/groups.ts).
 */

interface RootState {
  apps: { active: ChatApp; appSettings: any }
  groups: GroupsState
}
const getRoot = (): RootState => reduxStore.getState() as unknown as RootState

type Saga<P> = { payload: P }
type SagaWorker = (action: any) => any

interface GroupsListPayload {
  data?: ChatApp
  appSettings?: any
  currentPage?: number
  filters?: (GroupsListFilters & { createdAt?: [string, string]; guids?: string[]; type?: string[]; membersCount?: string[] }) | Record<string, unknown>
}

function buildServiceFilters(filters: Record<string, any>): GroupsListFilters {
  const serviceFilters: any = { ...filters }

  if (serviceFilters.createdAt && serviceFilters.createdAt.length === 2) {
    const [fromDate, toDate] = serviceFilters.createdAt
    serviceFilters.createdAtFrom = Math.floor(new Date(`${fromDate}T00:00:00`).getTime() / 1000)
    serviceFilters.createdAtTo = Math.floor(new Date(`${toDate}T23:59:59`).getTime() / 1000)
  }
  delete serviceFilters.createdAt

  if (serviceFilters.guids && Array.isArray(serviceFilters.guids)) {
    if (serviceFilters.guids.length === 1) {
      const [guid] = serviceFilters.guids
      serviceFilters.guid = guid
      delete serviceFilters.guids
    }
  }

  if (serviceFilters.type && Array.isArray(serviceFilters.type) && serviceFilters.type.length > 0) {
    const [typeValue] = serviceFilters.type
    serviceFilters.type = typeValue
  }

  if (
    serviceFilters.membersCount &&
    Array.isArray(serviceFilters.membersCount) &&
    serviceFilters.membersCount.length > 0
  ) {
    const [entry] = serviceFilters.membersCount
    const [operator, value] = entry.split(':')
    serviceFilters.membersCount = { operator, value: parseInt(value, 10) }
  }

  return serviceFilters as GroupsListFilters
}

export function* GROUPS_LIST({ payload }: Saga<GroupsListPayload>) {
  try {
    const filters = payload.filters || {}
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true, groupsFilters: filters } })
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings

    // Trimmed like SEARCH_GROUP — this serves page 2+ of an active search.
    const searchText = getRoot().groups.groupSearchText.trim()
    const groupsPagination = getRoot().groups.groupsListPagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && groupsPagination.current_page !== undefined)
      pageNo = groupsPagination.current_page + 1

    const serviceFilters = buildServiceFilters(filters as Record<string, any>)

    const success: GroupsListResponse | false = yield call(
      groupsList,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      searchText,
      serviceFilters,
    )

    if (success && success.data) {
      let totalGroupCount = success.data.length
      let groupsListPagination: LoosePagination | unknown[] = []

      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalGroupCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        groupsListPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          groupsList: success.data,
          groupsListTotal: totalGroupCount,
          groupsListPagination,
          areGroupsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { groupsList: [], groupsListTotal: 0, groupsListPagination: [], areGroupsLoading: false },
      })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to fetch groups', 'error')
  }
}

export function* ADD_GROUP({ payload }: Saga<Record<string, any>>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const filtered: Record<string, any> = {}
    _.forEach(payload, (n, k) => {
      if (payload[k] && _.trim(payload[k]) !== '') {
        filtered[k] = n
      }
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: GroupResponse = yield call(groupAdd, filtered as any, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      yield put({ type: actions.GROUPS_LIST, payload: { currentPage: 1, filters: {}, data: activeApp } })
      yield put(push(`/app/${activeApp.id}/groups`))
      window.triggerNotification('Group added', 'success')
    } else {
      yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })

      if (success.error?.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, value => {
          window.triggerNotification(value as string, 'error')
        })
      } else if (success.error?.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to add group', 'error')
  }
}

export function* GET_GROUP({ payload }: Saga<{ id: string }>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const { id } = payload
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: GroupResponse = yield call(groupGet, id, activeApp, getAppHost(appSettings))
    if (success && success.data) {
      yield put({ type: actions.SET_STATE, payload: { groupsActive: success.data, areGroupsLoading: false } })
    } else {
      yield put(push(`/app/${activeApp.id}/groups`))
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to fetch group', 'error')
  }
}

export function* UPDATE_GROUP({ id, payload }: { id: string; payload: Record<string, any> }) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })
    const filtered: Record<string, any> = {}
    const unset: string[] = []
    _.forEach(payload, (n, k) => {
      if (payload[k] && _.trim(payload[k]) !== '') {
        filtered[k] = n
      } else if (k === 'tags') {
        // ENG-37805: an empty tag list must be sent as `tags: []` so the API
        // clears it — listing it in `unset` leaves the existing tags in place.
        // Same fix users/sagas.ts UPDATE_USER already carries.
        filtered.tags = []
      } else {
        unset.push(k)
      }
    })
    filtered.unset = unset
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: GroupResponse = yield call(groupUpdate, id, filtered as any, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      // Deliberate deviation from apps/sagas.js, which redirected to the groups
      // list on save: the user stays on the detail page, so refresh the edited
      // group (Details card + drawer re-sync) and the list behind it. The
      // returned value reaches the dispatcher's `onComplete` (withCallback) so
      // the Edit drawer closes only on success.
      yield put({ type: actions.GET_GROUP, payload: { id } })
      yield put({ type: actions.GROUPS_LIST, payload: { currentPage: 1, data: activeApp } })
      window.triggerNotification('Group saved', 'success')
      return true
    }

    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    // `success?.` on the guards: this block sits at the top level of the saga
    // (the early `return true` handles the happy path), so a 2xx with an empty
    // body would reach here with `success` undefined. Matches the safer pattern
    // GET_GROUP_MEMBERS already uses.
    if (success?.error?.code === 'ERR_BAD_REQUEST') {
      _.forEach(success.error.details, value => {
        window.triggerNotification(value as string, 'error')
      })
    } else if (success?.error?.message !== undefined) {
      window.triggerNotification(success.error.message, 'error')
    } else {
      // Ported verbatim from apps/sagas.js:5111 — that's a genuine live-production
      // typo ('Uknown Error'); per D2 the exact string is preserved here.
      window.triggerNotification('Uknown Error', 'error')
    }
    return false
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to update group', 'error')
    return false
  }
}

export function* DELETE_GROUP({
  payload,
}: Saga<{ id: string; refresh?: boolean; filters?: Record<string, unknown> }>) {
  try {
    const { id, refresh, filters } = payload

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { groupsListPagination } = getRoot().groups

    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const success: SuccessMessageResponse = yield call(groupDelete, id, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      const groupListPageNo =
        groupsListPagination !== undefined && (groupsListPagination as LoosePagination).count! > 1
          ? (groupsListPagination as LoosePagination).current_page
          : 1
      yield put({
        type: actions.GROUPS_LIST,
        payload: { currentPage: groupListPageNo, filters: filters || {}, data: activeApp },
      })
      window.triggerNotification('Group deleted', 'success')
      if (refresh) yield put(push(`/app/${activeApp.id}/groups`))
    } else {
      yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
      if (success.error?.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(success.error.details?.name as string, 'error')
      }
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to delete group', 'error')
  }
}

export function* ENTER_GROUP_TEXT({ payload }: Saga<{ searchText?: string; clearText?: boolean }>) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({ type: actions.SET_STATE, payload: { groupSearchText: '' } })
      yield put({ type: actions.GROUPS_LIST, payload: { currentPage: 1 } })
    } else {
      yield put({ type: actions.SET_STATE, payload: { groupSearchText: searchText } })
      // Bug fix (manual QA, 2026-08-06): apps/sagas.js:5235 gates on
      // searchText.length === 0 || >= 2, so a single character never fires
      // SEARCH_GROUP. Matches the same deliberate exception already applied
      // to ENTER_GROUP_MEMBER_TEXT — search on any non-empty length.
      if (searchText !== undefined) {
        // Debounce — takeLatest below lets a newer keystroke cancel this delay,
        // so a fast typist fires one request instead of one per character.
        yield delay(300)
        yield put({ type: actions.SEARCH_GROUP, payload: {} })
      }
    }
  } catch (error) {
    // no-op — matches dormant + live behavior (this handler never surfaces an error)
  }
}

export function* SEARCH_GROUP() {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const searchText = getRoot().groups.groupSearchText

    // Trimmed at the call site only: state keeps what the user typed (it is the
    // input's value), the endpoint gets a term it can prefix-match, and the
    // stale-response guard below still compares raw against raw.
    const success: GroupsListResponse = yield call(
      groupsSearch,
      activeApp,
      getAppHost(appSettings),
      searchText.trim(),
    )

    // Stale-response guard (mirrors SEARCH_GROUP_MEMBER) — a slow response for
    // an abandoned query must not overwrite newer results.
    if (getRoot().groups.groupSearchText !== searchText) return

    if (success && success.data) {
      let totalGroupCount = success.data.length
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalGroupCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupsList: success.data,
          groupsListTotal: totalGroupCount,
          groupsListPagination: success.meta?.pagination,
          areGroupsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { groupsListTotal: 0, groupsListPagination: {}, areGroupsLoading: false },
      })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to search groups', 'error')
  }
}

export function* GET_GROUP_MEMBERS({
  payload,
}: Saga<{
  id: string
  data?: ChatApp
  appSettings?: any
  currentPage?: number
  /** Also re-fetch the group record. Only ADD_MEMBERS_TO_GROUP needs it; off by
   * default so mount/pagination/search don't duplicate GroupDetail's GET_GROUP. */
  refreshGroup?: boolean
}>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })
    const { id, refreshGroup } = payload
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings

    const searchText = getRoot().groups.groupMemberSearchText
    const groupMemberPagination = getRoot().groups.groupMembersListPagination as LoosePagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && groupMemberPagination.current_page !== undefined)
      pageNo = groupMemberPagination.current_page + 1

    const success: GroupMembersListResponse = yield call(
      groupGetMembers,
      id,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      // Trimmed at the call site only — the guards below compare raw to raw.
      searchText.trim(),
    )

    // Fix ported from apps/sagas.js:4410 — a stale in-flight response whose
    // search text no longer matches current input must not overwrite newer
    // results (the dormant version had no such guard).
    if (getRoot().groups.groupMemberSearchText !== searchText) return

    if (success && success.data) {
      let totalGroupMemberCount = success.data.length
      let groupMembersListPagination: LoosePagination | unknown[] = []
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalGroupMemberCount = success.meta.pagination.per_page * success.meta.pagination.total_pages
        groupMembersListPagination = success.meta.pagination
      }
      const groupData: GroupResponse | undefined = refreshGroup
        ? yield call(groupGet, id, payload.data, getAppHost(payload.appSettings))
        : undefined

      // Second guard, ported from apps/sagas.js:4421 — the follow-up groupGet
      // call is itself async; re-check before committing state.
      if (getRoot().groups.groupMemberSearchText !== searchText) return

      yield put({
        type: actions.SET_STATE,
        payload: {
          // `groupGet` maps a failed request to the API error payload, which has
          // no `data` — assigning it blindly put `undefined` into groupsActive and
          // crashed GroupDetail on the next render. Keep the current group instead.
          groupsActive: groupData?.data ?? getRoot().groups.groupsActive,
          groupMembersList: success.data,
          groupMembersListTotal: totalGroupMemberCount,
          groupMembersListPagination,
          areGroupsLoading: false,
        },
      })
    } else {
      // Fix ported from apps/sagas.js:4434-4436 — optional chaining, since a
      // failed request may have no `error` shape at all; the dormant version's
      // unguarded access threw here and skipped both the redirect and reset.
      if (success?.error?.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, value => {
          window.triggerNotification(value as string, 'error')
        })
      } else if (success?.error?.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
      yield put(push(`/app/${payload.data.id}/groups`))
      yield put({
        type: actions.SET_STATE,
        payload: {
          areGroupsLoading: false,
          groupMembersList: [],
          groupMembersListTotal: 0,
          groupMembersListPagination: [],
        },
      })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to fetch group members', 'error')
  }
}

export function* ENTER_GROUP_MEMBER_TEXT({ payload }: Saga<{ searchText?: string; clearText?: boolean }>) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({ type: actions.SET_STATE, payload: { groupMemberSearchText: '' } })
      const { guid } = getRoot().groups.groupsActive as ChatGroup

      yield put({ type: actions.GET_GROUP_MEMBERS, payload: { id: guid, currentPage: 1 } })
    } else {
      yield put({ type: actions.SET_STATE, payload: { groupMemberSearchText: searchText } })
      // Fix ported from apps/sagas.js:4491-4500 — debounce (watcher below
      // switches to takeLatest so a newer keystroke cancels this delay) and
      // search on any length, including a single character (dormant gated on
      // 0 || >=2, so a 1-char search never fired).
      yield delay(300)
      yield put({ type: actions.SEARCH_GROUP_MEMBER, payload: {} })
    }
  } catch (error) {
    // no-op — matches dormant + live behavior
  }
}

export function* SEARCH_GROUP_MEMBER() {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const activeGroup = getRoot().groups.groupsActive as ChatGroup
    const searchText = getRoot().groups.groupMemberSearchText

    const pageNo = 1

    const success: GroupMembersListResponse = yield call(
      groupMemberSearch,
      activeGroup.guid,
      // Trimmed at the call site only — the guard below compares raw to raw.
      searchText.trim(),
      activeApp,
      getAppHost(appSettings),
      pageNo,
    )

    // Fix ported from apps/sagas.js:4534 — stale-response guard.
    if (getRoot().groups.groupMemberSearchText !== searchText) return

    if (success && success.data) {
      let totalGroupMemberCount = success.data.length
      let groupMembersListPagination: LoosePagination | unknown[] = []

      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalGroupMemberCount = success.meta.pagination.per_page * success.meta.pagination.total_pages
        groupMembersListPagination = success.meta.pagination
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupsActive: activeGroup,
          groupMembersList: success.data,
          groupMembersListTotal: totalGroupMemberCount,
          groupMembersListPagination,
          areGroupsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupsActive: activeGroup,
          groupMembersList: [],
          groupMembersListTotal: 0,
          groupMembersListPagination: [],
          areGroupsLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to search group members', 'error')
  }
}

export function* ADD_MEMBERS_TO_GROUP({ payload }: Saga<{ uids: string[] }>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uids } = payload
    const { guid: addGUID } = getRoot().groups.groupsActive as ChatGroup
    const success: GroupMembersChangeResponse = yield call(
      addMembersToGroup,
      uids,
      addGUID,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      // refreshGroup: adding members changes `membersCount` on the group record.
      yield put({ type: actions.GET_GROUP_MEMBERS, payload: { id: addGUID, currentPage: 1, refreshGroup: true } })
      window.triggerNotification('User added to group', 'success')
    } else if (success.error?.message !== undefined) {
      if (success.error.code === 'ERR_GROUP_NO_CLEARANCE') {
        // Ported verbatim from apps/sagas.js:13466 (trailing space is a real
        // live-production quirk, preserved per D2).
        window.triggerNotification('Group action not allowed ', 'error')
      } else {
        window.triggerNotification(success.error.message, 'error')
      }
    } else {
      window.triggerNotification('Some error occurred', 'error')
    }

    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    return success
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to add members to group', 'error')
    return false
  }
}

interface NonParticipantsPayload {
  data?: ChatApp
  appSettings?: any
  currentPage?: number
}

// NON_PARTICIPANTS_LIST/SEARCH_NON_PARTICIPANT: confirmed via a direct read of
// apps/sagas.js that production reuses generic services/users list/search —
// there is no group-scoped "non-members" endpoint. See PARITY-AUDIT.md.
export function* NON_PARTICIPANTS_LIST({ payload }: Saga<NonParticipantsPayload>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings
    const searchText = getRoot().groups.nonParticipantSearchText
    const nonParticipantsPagination = getRoot().groups.nonParticipantsListPagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && nonParticipantsPagination.current_page !== undefined)
      pageNo = nonParticipantsPagination.current_page + 1
    const success: UsersListResponse | false = yield call(
      usersList,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      // Trimmed at the call site only — state keeps what the user typed.
      searchText?.trim() || '',
    )

    if (success && success.data) {
      let totalUserCount = success.data.length
      let nonParticipantsListPagination: LoosePagination | unknown[] = []

      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        nonParticipantsListPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          nonParticipantsList: success.data,
          nonParticipantsListTotal: totalUserCount,
          nonParticipantsListPagination,
          areGroupsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonParticipantsList: [],
          nonParticipantsListTotal: 0,
          nonParticipantsListPagination: {},
          areGroupsLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to fetch non-participants', 'error')
  }
}

export function* ENTER_NON_PARTICIPANT_TEXT({ payload }: Saga<{ searchText?: string; clearText?: boolean }>) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({ type: actions.SET_STATE, payload: { nonParticipantSearchText: '' } })
      yield put({ type: actions.SEARCH_NON_PARTICIPANT, payload: {} })
    } else {
      yield put({ type: actions.SET_STATE, payload: { nonParticipantSearchText: searchText } })
      yield put({ type: actions.SEARCH_NON_PARTICIPANT, payload: {} })
    }
  } catch (error) {
    // no-op — matches dormant + live behavior
  }
}

export function* SEARCH_NON_PARTICIPANT() {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const searchText = getRoot().groups.nonParticipantSearchText
    const success: UsersListResponse | false = yield call(
      usersSearch,
      activeApp,
      getAppHost(appSettings),
      // Trimmed at the call site only — state keeps what the user typed.
      searchText?.trim() || '',
    )

    if (success && success.data) {
      let totalUserCount = success.data.length
      let nonParticipantsListPagination: LoosePagination | unknown[] = []
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        nonParticipantsListPagination = success.meta.pagination
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonParticipantsList: success.data,
          nonParticipantsListTotal: totalUserCount,
          nonParticipantsListPagination,
          areGroupsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { nonParticipantsListTotal: 0, nonParticipantsListPagination: {}, areGroupsLoading: false },
      })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to search non-participants', 'error')
  }
}

// CHANGE_MEMBER_SCOPE: confirmed via a direct read of apps/sagas.js that
// production imports and reuses services/users/groups.ts's updateScopeInGroup
// directly (POST /groups/{guid}/members with {admins/moderators/participants:
// [uid]}) — not a per-member PUT. See PARITY-AUDIT.md.
export function* CHANGE_MEMBER_SCOPE({ payload }: Saga<{ uid: string; scope: string }>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { guid: addGUID } = getRoot().groups.groupsActive as ChatGroup
    const { uid, scope } = payload

    const success: UserGroupMembersChangeResponse = yield call(
      changeScopeInGroup,
      uid,
      addGUID,
      scope,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      yield put({ type: actions.GET_GROUP_MEMBERS, payload: { id: addGUID, currentPage: 1 } })
      window.triggerNotification(`Member is now a ${scope}`, 'success')
    } else {
      yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })

      if (success.error?.message !== undefined) {
        if (success.error.code === 'ERR_GROUP_NO_CLEARANCE') {
          window.triggerNotification('Group action not allowed', 'error')
        } else {
          window.triggerNotification(success.error.message, 'error')
        }
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
    return success
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to change member scope', 'error')
    return false
  }
}

export function* GET_BANNED_GROUP_MEMBERS({
  payload,
}: Saga<{ id: string; data?: ChatApp; appSettings?: any; currentPage?: number }>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const { id } = payload
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings

    const searchText = getRoot().groups.groupBannedMemberSearchText
    const groupBannedMemberPagination = getRoot().groups.groupBannedMembersListPagination as LoosePagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && groupBannedMemberPagination.current_page !== undefined)
      pageNo = groupBannedMemberPagination.current_page + 1

    const success: GroupMembersListResponse = yield call(
      groupGetBannedMembers,
      id,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      // Trimmed at the call site only — state keeps what the user typed.
      searchText.trim(),
    )

    if (success && success.data) {
      let totalGroupBannedMemberCount = success.data.length
      let groupBannedMembersListPagination: LoosePagination | unknown[] = []

      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalGroupBannedMemberCount = success.meta.pagination.per_page * success.meta.pagination.total_pages
        groupBannedMembersListPagination = success.meta.pagination
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupBannedMembersList: success.data,
          groupBannedMembersListTotal: totalGroupBannedMemberCount,
          groupBannedMembersListPagination,
          areGroupsLoading: false,
        },
      })
    } else {
      if (success?.error?.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, value => {
          window.triggerNotification(value as string, 'error')
        })
      } else if (success?.error?.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
      yield put({
        type: actions.SET_STATE,
        payload: { areGroupsLoading: false, groupBannedMembersListTotal: 0, groupBannedMembersListPagination: [] },
      })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to fetch banned members', 'error')
  }
}

export function* ENTER_BANNED_GROUP_MEMBER_TEXT({ payload }: Saga<{ searchText?: string; clearText?: boolean }>) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({ type: actions.SET_STATE, payload: { groupBannedMemberSearchText: '' } })
      const { guid } = getRoot().groups.groupsActive as ChatGroup
      yield put({ type: actions.GET_BANNED_GROUP_MEMBERS, payload: { id: guid, currentPage: 1 } })
    } else {
      yield put({ type: actions.SET_STATE, payload: { groupBannedMemberSearchText: searchText } })
      // Bug fix (manual QA, 2026-08-06): apps/sagas.js:4673 gates on
      // searchText.length === 0 || >= 2, so a single character never fires
      // SEARCH_BANNED_GROUP_MEMBER. Matches the same deliberate exception
      // already applied to ENTER_GROUP_MEMBER_TEXT — search on any non-empty length.
      if (searchText !== undefined) {
        yield put({ type: actions.SEARCH_BANNED_GROUP_MEMBER, payload: {} })
      }
    }
  } catch (error) {
    // no-op — matches dormant + live behavior
  }
}

export function* SEARCH_BANNED_GROUP_MEMBER() {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const activeGroup = getRoot().groups.groupsActive as ChatGroup
    const searchText = getRoot().groups.groupBannedMemberSearchText

    const success: GroupMembersListResponse = yield call(
      groupBannedMemberSearch,
      activeGroup.guid,
      // Trimmed at the call site only — state keeps what the user typed.
      searchText.trim(),
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      let totalGroupBannedMemberCount = success.data.length

      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalGroupBannedMemberCount = success.meta.pagination.per_page * success.meta.pagination.total_pages
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupsActive: activeGroup,
          groupBannedMembersList: success.data,
          groupBannedMembersListTotal: totalGroupBannedMemberCount,
          areGroupsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupsActive: activeGroup,
          groupBannedMembersList: [],
          groupBannedMembersListTotal: 0,
          areGroupsLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to search banned members', 'error')
  }
}

export function* BAN_GROUP_MEMBER({ payload }: Saga<{ uid: string; guid: string }>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const { uid, guid } = payload
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: SuccessMessageResponse = yield call(groupBanMember, uid, guid, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      const groupMemberPagination = getRoot().groups.groupMembersListPagination as LoosePagination
      const groupMembersListPrev = getRoot().groups.groupMembersList
      const currentPageItemCount = groupMembersListPrev.length
      const pageNo =
        groupMemberPagination.current_page !== undefined && currentPageItemCount !== 1
          ? groupMemberPagination.current_page
          : 1
      const pageNoForBannedGroupMembers = 1

      const memberSuccess: GroupMembersListResponse = yield call(
        groupGetMembers,
        guid,
        activeApp,
        getAppHost(appSettings),
        pageNo,
      )

      const bannedMemberSuccess: GroupMembersListResponse = yield call(
        groupGetBannedMembers,
        guid,
        activeApp,
        getAppHost(appSettings),
        pageNoForBannedGroupMembers,
      )
      const groupData: GroupResponse = yield call(groupGet, guid, activeApp, getAppHost(appSettings))
      let totalGroupMemberCount = memberSuccess.data!.length
      let totalBannedGroupMemberCount = bannedMemberSuccess.data!.length

      let groupMembersListPagination: LoosePagination | unknown[] = []
      let groupBannedMembersListPagination: LoosePagination | unknown[] = []

      if (memberSuccess.meta?.pagination?.total_pages) {
        totalGroupMemberCount = memberSuccess.meta.pagination.per_page * memberSuccess.meta.pagination.total_pages
        groupMembersListPagination = memberSuccess.meta.pagination
      }

      if (bannedMemberSuccess.meta?.pagination?.total_pages) {
        totalBannedGroupMemberCount =
          bannedMemberSuccess.meta.pagination.per_page * bannedMemberSuccess.meta.pagination.total_pages
        groupBannedMembersListPagination = bannedMemberSuccess.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          // `groupGet` maps a failed request to the API error payload, which has
          // no `data` — assigning it blindly put `undefined` into groupsActive and
          // crashed GroupDetail on the next render. Keep the current group instead.
          groupsActive: groupData?.data ?? getRoot().groups.groupsActive,
          groupMembersList: memberSuccess.data,
          groupBannedMembersList: bannedMemberSuccess.data,
          groupMembersListTotal: totalGroupMemberCount,
          groupBannedMembersListTotal: totalBannedGroupMemberCount,
          groupMembersListPagination,
          groupBannedMembersListPagination,
          areGroupsLoading: false,
        },
      })
      window.triggerNotification('User has been banned', 'success')
    } else {
      if (success.error?.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, value => {
          window.triggerNotification(value as string, 'error')
        })
      } else if (success.error?.message !== undefined) {
        // Ported verbatim from apps/sagas.js:4843 (double-n typo is a real
        // live-production quirk, preserved per D2).
        window.triggerNotification('User cannnot be banned', 'error')
      } else {
        window.triggerNotification('Unknown error', 'error')
        yield put(push(`/app/${activeApp.id}/groups`))
      }
      yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
      return false
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to ban member', 'error')
    return false
  }
}

// UNBAN_GROUP_MEMBER: deliberate D2 exception. apps/sagas.js's error branch
// (L4947-4967) never resets loading:false (stuck spinner on failure) and one
// error path dispatches a `type:'success'` toast for a failure — both are
// real production bugs. This ported version keeps the dormant slice's already
// -correct behavior instead of reproducing them. See PARITY-AUDIT.md.
export function* UNBAN_GROUP_MEMBER({ payload }: Saga<{ uid: string; guid: string }>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const { uid, guid } = payload
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: SuccessMessageResponse = yield call(
      groupUnbanMember,
      uid,
      guid,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      const groupBannedMemberPagination = getRoot().groups.groupBannedMembersListPagination as LoosePagination
      const pageNoForGroupMembers = 1

      const memberSuccess: GroupMembersListResponse = yield call(
        groupGetMembers,
        guid,
        activeApp,
        getAppHost(appSettings),
        pageNoForGroupMembers,
      )
      const groupBannedMembersListPrev = getRoot().groups.groupBannedMembersList

      const currentPageItemCount = groupBannedMembersListPrev.length
      const pageNo =
        groupBannedMemberPagination.current_page !== undefined && currentPageItemCount !== 1
          ? groupBannedMemberPagination.current_page
          : 1

      const bannedMemberSuccess: GroupMembersListResponse = yield call(
        groupGetBannedMembers,
        guid,
        activeApp,
        getAppHost(appSettings),
        pageNo,
      )
      const groupData: GroupResponse = yield call(groupGet, guid, activeApp, getAppHost(appSettings))
      let totalGroupMemberCount = memberSuccess.data!.length
      let totalBannedGroupMemberCount = bannedMemberSuccess.data!.length
      let groupMembersListPagination: LoosePagination | unknown[] = []
      let groupBannedMembersListPagination: LoosePagination | unknown[] = []
      if (memberSuccess.meta?.pagination?.total_pages) {
        totalGroupMemberCount = memberSuccess.meta.pagination.per_page * memberSuccess.meta.pagination.total_pages
        groupMembersListPagination = memberSuccess.meta.pagination
      }
      if (bannedMemberSuccess.meta?.pagination?.total_pages) {
        totalBannedGroupMemberCount =
          bannedMemberSuccess.meta.pagination.per_page * bannedMemberSuccess.meta.pagination.total_pages
        groupBannedMembersListPagination = bannedMemberSuccess.meta.pagination
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          // `groupGet` maps a failed request to the API error payload, which has
          // no `data` — assigning it blindly put `undefined` into groupsActive and
          // crashed GroupDetail on the next render. Keep the current group instead.
          groupsActive: groupData?.data ?? getRoot().groups.groupsActive,
          groupMembersList: memberSuccess.data,
          groupBannedMembersList: bannedMemberSuccess.data,
          groupMembersListTotal: totalGroupMemberCount,
          groupBannedMembersListTotal: totalBannedGroupMemberCount,
          groupMembersListPagination,
          groupBannedMembersListPagination,
          areGroupsLoading: false,
        },
      })
      window.triggerNotification('User has been unbanned', 'success')
    } else {
      if (success.error?.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, value => {
          window.triggerNotification(value as string, 'error')
        })
      } else if (success.error?.message !== undefined) {
        window.triggerNotification('User could not be unbanned', 'error')
      } else {
        window.triggerNotification('Unknown Error', 'error')
        yield put(push(`/app/${activeApp.id}/groups/${guid}`))
      }
      // Loading flag IS reset here (unlike apps/sagas.js) — see the D2
      // exception note above the function.
      yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
      return false
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to unban member', 'error')
    return false
  }
}

export function* KICK_GROUP_MEMBER({ payload }: Saga<{ uid: string; guid: string }>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const { uid, guid } = payload
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: SuccessMessageResponse = yield call(groupKickMember, uid, guid, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      const groupMemberPagination = getRoot().groups.groupMembersListPagination as LoosePagination
      const groupMembersListPrev = getRoot().groups.groupMembersList

      const currentPageItemCount = groupMembersListPrev.length
      const pageNo =
        groupMemberPagination.current_page !== undefined && currentPageItemCount !== 1
          ? groupMemberPagination.current_page
          : 1
      const memberSuccess: GroupMembersListResponse = yield call(
        groupGetMembers,
        guid,
        activeApp,
        getAppHost(appSettings),
        pageNo,
      )
      const groupData: GroupResponse = yield call(groupGet, guid, activeApp, getAppHost(appSettings))
      let totalGroupMemberCount = memberSuccess.data!.length
      let groupMembersListPagination: LoosePagination | unknown[] = []
      if (memberSuccess.meta?.pagination?.total_pages) {
        totalGroupMemberCount = memberSuccess.meta.pagination.per_page * memberSuccess.meta.pagination.total_pages
        groupMembersListPagination = memberSuccess.meta.pagination
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          // `groupGet` maps a failed request to the API error payload, which has
          // no `data` — assigning it blindly put `undefined` into groupsActive and
          // crashed GroupDetail on the next render. Keep the current group instead.
          groupsActive: groupData?.data ?? getRoot().groups.groupsActive,
          groupMembersList: memberSuccess.data,
          groupMembersListTotal: totalGroupMemberCount,
          groupMembersListPagination,
          areGroupsLoading: false,
        },
      })
      window.triggerNotification('User has been removed', 'success')
    } else {
      if (success.error?.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, value => {
          window.triggerNotification(value as string, 'error')
        })
      } else if (success.error?.message !== undefined) {
        window.triggerNotification('User could not be removed', 'error')
      } else {
        window.triggerNotification('Unknown Error', 'error')
        yield put(push(`/app/${activeApp.id}/groups/${guid}`))
      }
      yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
      return false
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to remove member', 'error')
    return false
  }
}

export function* GROUP_SCOPE_PERMISSIONS_LIST({ payload }: Saga<{ guid: string; scope: string }>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: ScopePermissionsListResponse = yield call(
      listGroupScopePermissions,
      payload.guid,
      payload.scope,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data && success.data.permissions) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupScopePermissionsList: success.data.permissions,
          groupScopePermissionsListTotal: Object.keys(success.data.permissions).length || 0,
          areGroupsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { groupScopePermissionsList: [], groupScopePermissionsListTotal: 0, areGroupsLoading: false },
      })
      if (success.error?.message) {
        window.triggerNotification(success.error.message, 'error')
      }
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to fetch scope permissions', 'error')
  }
}

export function* GROUP_SCOPE_PERMISSIONS_VALUES_GET({ payload }: Saga<{ guid: string; scope: string }>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const { guid, scope } = payload
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: ScopePermissionsValuesResponse = yield call(
      getGroupScopePermissionsValues,
      guid,
      scope,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data && success.data.permissions) {
      yield put({
        type: actions.SET_STATE,
        payload: { groupScopePermissionsValues: success.data.permissions, areGroupsLoading: false },
      })
    } else {
      yield put({ type: actions.SET_STATE, payload: { groupScopePermissionsValues: {}, areGroupsLoading: false } })
      if (success.error?.message) {
        window.triggerNotification(success.error.message, 'error')
      }
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to fetch scope permission values', 'error')
  }
}

export function* GROUP_SCOPE_PERMISSIONS_UPDATE({
  guid,
  scope,
  payload,
}: {
  guid: string
  scope: string
  payload: { permissions: Record<string, string | string[]> }
}) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: ScopePermissionsValuesResponse = yield call(
      updateGroupScopePermissions,
      guid,
      scope,
      payload,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data && success.data.permissions) {
      yield put({
        type: actions.SET_STATE,
        payload: { groupScopePermissionsValues: success.data.permissions, areGroupsLoading: false },
      })
      window.triggerNotification('Permissions updated successfully', 'success')
      // Reaches the dispatcher's `onComplete` (withCallback) so the editor
      // leaves edit mode only on a successful save. Every branch must return.
      return true
    }
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    // `success?.` — this now sits at the saga's top level (the early `return
    // true` handles the happy path), so a 2xx with an empty body reaches here
    // with `success` undefined. Same guard UPDATE_GROUP uses.
    if (success?.error?.message) {
      window.triggerNotification(success.error.message, 'error')
    } else {
      window.triggerNotification('Failed to update permissions', 'error')
    }
    return false
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to update permissions', 'error')
    return false
  }
}

export function* GROUP_SCOPE_PERMISSIONS_RESET({
  guid,
  scope,
  permissionId,
}: {
  guid: string
  scope: string
  permissionId: string
}) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: true } })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps

    const success: ScopePermissionsValuesResponse = yield call(
      resetGroupScopePermission,
      guid,
      scope,
      permissionId,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data && success.data.permissions) {
      yield put({
        type: actions.SET_STATE,
        payload: { groupScopePermissionsValues: success.data.permissions, areGroupsLoading: false },
      })
      window.triggerNotification('Permission reset to default', 'success')
    } else {
      yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
      if (success.error?.message) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Failed to reset permission', 'error')
      }
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areGroupsLoading: false } })
    window.triggerNotification('Failed to reset permission', 'error')
  }
}

export default function* groupsSaga() {
  yield all([
    takeEvery(actions.GROUPS_LIST, GROUPS_LIST as SagaWorker),
    takeEvery(actions.ADD_GROUP, ADD_GROUP as SagaWorker),
    takeEvery(actions.GET_GROUP, GET_GROUP as SagaWorker),
    // withCallback so the Edit drawer can close itself only on a successful save.
    takeEvery(actions.UPDATE_GROUP, withCallback(UPDATE_GROUP) as SagaWorker),
    takeEvery(actions.DELETE_GROUP, DELETE_GROUP as SagaWorker),
    // takeLatest, required for the handler's debounce to cancel — same as
    // ENTER_GROUP_MEMBER_TEXT. Was takeEvery: every keystroke searched.
    takeLatest(actions.ENTER_GROUP_TEXT, ENTER_GROUP_TEXT as SagaWorker),
    // Fixed per PARITY-AUDIT.md: apps/sagas.js registers takeEvery, dormant had takeLatest.
    takeEvery(actions.SEARCH_GROUP, SEARCH_GROUP as SagaWorker),
    takeEvery(actions.GET_GROUP_MEMBERS, GET_GROUP_MEMBERS as SagaWorker),
    // Fixed per PARITY-AUDIT.md: apps/sagas.js uses takeLatest + in-handler debounce
    // (required for the debounce-cancel to work); dormant had takeEvery, no debounce.
    takeLatest(actions.ENTER_GROUP_MEMBER_TEXT, ENTER_GROUP_MEMBER_TEXT as SagaWorker),
    // Fixed per PARITY-AUDIT.md: apps/sagas.js registers takeEvery, dormant had takeLatest.
    takeEvery(actions.SEARCH_GROUP_MEMBER, SEARCH_GROUP_MEMBER as SagaWorker),
    // Fixed per PARITY-AUDIT.md: apps/sagas.js wraps with withCallback, dormant didn't.
    takeEvery(actions.ADD_MEMBERS_TO_GROUP, withCallback(ADD_MEMBERS_TO_GROUP) as SagaWorker),
    takeEvery(actions.NON_PARTICIPANTS_LIST, NON_PARTICIPANTS_LIST as SagaWorker),
    takeEvery(actions.ENTER_NON_PARTICIPANT_TEXT, ENTER_NON_PARTICIPANT_TEXT as SagaWorker),
    // Fixed per PARITY-AUDIT.md: apps/sagas.js registers takeEvery, dormant had takeLatest.
    takeEvery(actions.SEARCH_NON_PARTICIPANT, SEARCH_NON_PARTICIPANT as SagaWorker),
    // Fixed per PARITY-AUDIT.md: apps/sagas.js wraps with withCallback, dormant didn't.
    takeEvery(actions.CHANGE_MEMBER_SCOPE, withCallback(CHANGE_MEMBER_SCOPE) as SagaWorker),
    takeEvery(actions.GET_BANNED_GROUP_MEMBERS, GET_BANNED_GROUP_MEMBERS as SagaWorker),
    takeEvery(actions.ENTER_BANNED_GROUP_MEMBER_TEXT, ENTER_BANNED_GROUP_MEMBER_TEXT as SagaWorker),
    // Fixed per PARITY-AUDIT.md: apps/sagas.js registers takeEvery, dormant had takeLatest.
    takeEvery(actions.SEARCH_BANNED_GROUP_MEMBER, SEARCH_BANNED_GROUP_MEMBER as SagaWorker),
    takeEvery(actions.BAN_GROUP_MEMBER, BAN_GROUP_MEMBER as SagaWorker),
    takeEvery(actions.UNBAN_GROUP_MEMBER, UNBAN_GROUP_MEMBER as SagaWorker),
    takeEvery(actions.KICK_GROUP_MEMBER, KICK_GROUP_MEMBER as SagaWorker),
    takeEvery(actions.GROUP_SCOPE_PERMISSIONS_LIST, GROUP_SCOPE_PERMISSIONS_LIST as SagaWorker),
    takeEvery(actions.GROUP_SCOPE_PERMISSIONS_VALUES_GET, GROUP_SCOPE_PERMISSIONS_VALUES_GET as SagaWorker),
    // withCallback so the permissions editor leaves edit mode only on a successful save.
    takeEvery(actions.GROUP_SCOPE_PERMISSIONS_UPDATE, withCallback(GROUP_SCOPE_PERMISSIONS_UPDATE) as SagaWorker),
    takeEvery(actions.GROUP_SCOPE_PERMISSIONS_RESET, GROUP_SCOPE_PERMISSIONS_RESET as SagaWorker),
  ])
}
